import styled from '@emotion/styled';
import { Col, Input, Rate, Row, Table, Tabs } from 'antd';

import { color, font, layout, mediaQueries, shadow } from 'src/shared/styles/variables';

export const Strong = styled('span')(
    {
        color: color.blueLagoon,
    },
    font.medium(font.size.medium),
);

export const TextLink = styled('a')(
    {
        color: color.bayOfMany,
        textDecoration: 'underline',
        '&:hover': {
            color: color.teal,
        },
    },
    font.heavy(font.size.medium),
);

interface StepBoxWrapperProps {
    boxShadow?: boolean;
    noTopMargin?: boolean;
}

export const StepBoxWrapper = styled('div')<StepBoxWrapperProps>(({ boxShadow, noTopMargin }) => ({
    backgroundColor: color.white,
    padding: '1.31rem 1.31rem 1.75rem',
    borderRadius: 7.2,
    marginTop: noTopMargin ? 0 : '1.75rem',
    boxShadow: boxShadow ? shadow.lightCardShadow : 'none',
}));

export const ContentRow = styled(Row)({
    width: '100%',
    maxWidth: layout.containerSmallerWidth,
    margin: '0 auto',
    padding: '2.63rem 0.88rem',
    '& + &': {
        paddingTop: 0,
    },
    '&:last-of-type': {
        paddingBottom: '3.50rem',
    },
});

interface ContentColumnProps {
    textalign?: any;
}

export const ContentColumn = styled(Col)<ContentColumnProps>(({ textalign = 'left' }) => ({
    textAlign: textalign,
    padding: '0rem 0.88rem',
}));

interface StepHeadingProps {
    small?: boolean;
}

export const StepHeading = styled('h2')(
    (props: StepHeadingProps) =>
        props.small
            ? {
                  ...font.book(font.size.medium),
                  color: color.mamba,
                  margin: '0rem 0rem 0.44rem',
              }
            : {
                  color: color.chathamsBlue,
                  margin: '0rem 0rem 0.88rem',
                  ...font.heavy(font.size.header),
              },
    {
        '& strong': {
            color: color.blueLagoon,
        },
    },
);

interface StepDescriptionProps {
    small?: boolean;
}

export const StepDescription = styled('p')(
    {
        margin: '0rem 0rem 1.75rem',
        lineHeight: 1.7,
        color: color.tundora,
    },
    font.book(font.size.medium),
    (props: StepDescriptionProps) =>
        props.small
            ? {
                  marginBottom: '0.88rem',
              }
            : {
                  fontSize: font.size.large,
              },
);

interface StepIconProps {
    iconUrl: string;
    centered?: boolean;
    large?: boolean;
}

export const StepIcon = styled('i')<StepIconProps>(({ iconUrl, centered, large }) => ({
    display: 'block',
    backgroundImage: `url('${iconUrl}')`,
    width: large ? '26.25rem' : '17.50rem',
    maxWidth: '100%',
    height: large ? '26.25rem' : '17.50rem',
    margin: '0 auto',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    [mediaQueries.md]: {
        margin: `1.75rem ${centered ? 'auto' : 0} 0`,
    },
    [mediaQueries.sm]: {
        width: '100%',
    },
}));

export const BulletList = styled('ul')({
    listStyleType: 'none',
    margin: 0,
    paddingLeft: '1.31rem',
});

interface BulletListElementProps {
    isSmall?: boolean;
}

export const BulletListElement = styled('li')<BulletListElementProps>(({ isSmall }) => ({
    ...font.book(isSmall ? font.size.base : font.size.medium),
    position: 'relative',
    marginBottom: '0.88rem',
    lineHeight: 1.5,

    '&:before': {
        content: '""',
        position: 'absolute',
        left: -20,
        top: isSmall ? 5 : 7,
        width: '0.44rem',
        height: '0.44rem',
        borderRadius: '100%',
        backgroundColor: color.bayOfMany,
    },
}));

export const FeedbackHeader = styled('h3')(
    {
        color: color.chathamsBlue,
    },
    font.heavy(font.size.medium),
);

export const StyledRate = styled(Rate)({
    '&&': {
        paddingLeft: 0,
        '& li': {
            fontSize: font.size.headerMedium,
            color: color.scooter,
            '&:not(:last-child)': {
                marginBottom: 0,
            },
        },
    },
});

export const TextArea = styled(Input.TextArea)({
    '&&.ant-input': {
        maxWidth: '45.50rem',
        marginTop: '0.35rem',
        margin: '0.44rem 0rem 0.88rem',
        padding: '0.66rem 0.88rem',
        resize: 'none',
        borderRadius: 4,
        border: `solid 1px ${color.paleSlate}`,
    },
});

export interface StyledTabsProps {
    noBarMargin?: boolean;
    noBorder?: boolean;
    tabsAmount?: number;
}

export const StyledTabs = styled(Tabs)<StyledTabsProps>(
    ({ noBarMargin, noBorder, tabsAmount }) => ({
        marginBottom: '2.19rem',

        '&&.ant-tabs-top': {
            padding: 0,
        },
        '&& .ant-tabs-bar': {
            borderBottom: noBorder ? 'none' : `1px solid ${color.gallery}`,
            marginBottom: noBarMargin ? 0 : '0.88rem',
        },
        '&& .ant-tabs-nav-wrap': {
            maxWidth: layout.containerSmallerWidth,
            margin: '0 auto -1px',
            padding: noBarMargin ? 0 : '0 1.75rem',
        },
        '&& .ant-tabs-nav-container-scrolling .ant-tabs-nav-wrap': {
            [mediaQueries.md]: {
                padding: 0,
            },
        },
        '&& .ant-tabs-nav': {
            width: '100%',
        },
        '&& .ant-tabs-tab': {
            ...font.medium(font.size.base),
            maxWidth: tabsAmount ? `${Math.floor((1 / tabsAmount) * 100)}%` : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            verticalAlign: 'top',
            color: color.baliHai,
            margin: '0',
            padding: '0.66rem 1.31rem 0.53rem',
            borderRadius: '5px 5px 0 0',
            border: `1px solid ${color.gallery}`,
            textTransform: 'capitalize',
            transition: 'all 0.2s',

            '&:not(:first-child)': {
                left: -1,
            },
            '&:hover': {
                color: color.tundora,
            },
            '&.ant-tabs-tab-active': {
                ...font.heavy(font.size.base),
                color: color.white,
                backgroundColor: color.blueLagoon,
                borderColor: color.blueLagoon,
            },
        },
        '&& .ant-tabs-ink-bar': {
            display: 'none !important', // antd is using element style, no other way to overload
        },
    }),
);

export const TabPane = Tabs.TabPane;

interface TabPaneContentProps {
    noPadding?: boolean;
}
export const TabPaneContent = styled('div')<TabPaneContentProps>(({ noPadding }) => ({
    maxWidth: layout.containerSmallerWidth,
    margin: '0 auto',
    padding: noPadding ? 0 : '1.75rem',
}));

export const PlannedActivitiesHolder = styled('div')({
    backgroundColor: color.aquaSpring,
    borderRadius: '0.26rem',
    padding: '1.75rem',
    marginBottom: '1.31rem',
});

interface PlannedActivitiesHeadingProps {
    noUnderline?: boolean;
}

export const PlannedActivitiesHeading = styled('h4')<PlannedActivitiesHeadingProps>(
    ({ noUnderline }) => ({
        color: color.chathamsBlue,
        paddingBottom: '0.88rem',
        borderBottom: noUnderline ? 'none' : `1px solid ${color.mariner}`,
        marginBottom: noUnderline ? 0 : '1.75rem',
        width: '100%',
    }),
    font.heavy(font.size.mediumExtended),
);

interface PlannedActivityProps {
    smallPadding?: boolean;
}

export const PlannedActivity = styled('div')<PlannedActivityProps>(({ smallPadding }) => ({
    paddingLeft: '1.75rem',
    position: 'relative',
    '&:not(:last-child)': {
        marginBottom: smallPadding ? '0.44rem' : '1.75rem',
    },
    '&:before': {
        position: 'absolute',
        content: '""',
        width: '0.44rem',
        height: '0.44rem',
        backgroundColor: color.scooter,
        borderRadius: 2,
        left: '0.22rem',
        top: '0.35rem',
    },
}));

export const ActivityName = styled('h5')(
    {
        color: color.chathamsBlue,
        marginBottom: 0,
        '::first-letter': {
            textTransform: 'uppercase',
        },
    },
    font.heavy(font.size.medium),
);

export const ActivityDescription = styled('p')(
    {
        color: color.mamba,
    },
    font.book(font.size.base),
);

export const ModalContent = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4.38rem',
});

export const StyledLink = styled('a')({
    color: color.blueLagoon,
});

interface ActivityContentProps {
    isNested?: boolean;
}

export const ActivityContent = styled('p')<ActivityContentProps>(
    ({ isNested }) => ({
        whiteSpace: isNested ? 'pre-wrap' : 'normal',
        color: color.chathamsBlue,
        paddingBottom: '0.88rem',
        marginBottom: '0.88rem',
        width: '100%',
    }),
    font.heavy(font.size.medium),
);

interface StyledWordProps {
    underline?: boolean;
}

export const StyledWord = styled('span')<StyledWordProps>(
    ({ underline }) => ({
        color: color.blueLagoon,
        textDecoration: underline ? 'underline' : 'none',
    }),
    font.heavy(font.size.medium),
);

export const StepImg = styled('img')({
    display: 'block',
    margin: '0 auto',
    [mediaQueries.md]: {
        width: '70%',
    },
    [mediaQueries.sm]: {
        width: '100%',
    },
});

interface ModalIconProps {
    iconUrl: string;
}

export const ModalIcon = styled('i')<ModalIconProps>(props => ({
    display: 'block',
    backgroundImage: `url(${props.iconUrl})`,
    height: '10.50rem',
    width: '10.50rem',
    margin: '0 auto 1.75rem',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
}));

export const ModalTitle = styled('h3')(
    {
        width: '100%',
        margin: '0 0 1.31rem',
        color: color.bayOfMany,
    },
    font.heavy(font.size.extraLarge),
);

export const ModalText = styled('p')(
    {
        color: color.tundora,
        marginBottom: '1.75rem',
    },
    font.book(font.size.large),
);

export const StyledOrderedList = styled('ol')({
    padding: '0.88rem',
});

export const StyledOrderedListElement = styled('li')(
    {
        whiteSpace: 'pre-wrap',
        marginBottom: '1.31rem',
        color: color.blueLagoon,
    },
    font.medium(font.size.medium),
);

interface StyledTableProps {
    coloredFirstCol?: boolean;
}

export const StyledTable = styled<any>(Table)<StyledTableProps>(
    {
        padding: '0.88rem',
        whiteSpace: 'pre-line',
        '.ant-table th': {
            backgroundColor: color.blueLagoon,
            color: color.white,
            fontWeight: 800,
        },
        '.ant-table-tbody tr': {
            color: color.blueLagoon,
            fontWeight: 800,
        },
        '.ant-table-tbody tr:nth-child(even)': {
            backgroundColor: color.aquaSpring,
        },
    },
    ({ coloredFirstCol }) =>
        coloredFirstCol && {
            '.ant-table-tbody td:first-child, &:hover .ant-table-tbody td:first-child': {
                backgroundColor: color.scooter,
                color: color.white,
                fontWeight: 800,
            },
            '.ant-table-tbody tr': {
                color: color.waikawaGray,
                fontWeight: 400,
            },
        },
);
